import { Box, Button, Divider, Paper, Stack, Typography } from '@mui/material';
import {
  Description as FileIcon,
  Folder as FolderIcon,
  InsertLink as LinkIcon,
} from '@mui/icons-material';
import type { FileStorageItem } from '../../api/fileStorageApi';
import { HighlightText } from '../../../../components/common/HighlightText';

const fileIcon = (item: FileStorageItem) => {
  if (item.isFolder) return <FolderIcon fontSize="small" color="warning" />;
  if (item.isUrl) return <LinkIcon fontSize="small" color="info" />;
  return <FileIcon fontSize="small" color="action" />;
};

type FileStorageSearchResultsProps = {
  activeSearch: string;
  items: FileStorageItem[];
  actionLabel: string;
  onAction: (item: FileStorageItem) => void;
};

export const FileStorageSearchResults = ({
  activeSearch,
  items,
  actionLabel,
  onAction,
}: FileStorageSearchResultsProps) => {
  if (!activeSearch) return null;

  return (
    <Paper variant="outlined" sx={{ mt: 1, p: 1 }}>
      <Typography variant="caption" color="text.secondary">
        Search results for "{activeSearch}"
      </Typography>
      {items.length === 0 ? (
        <Typography variant="body2" sx={{ mt: 0.75 }}>
          No data
        </Typography>
      ) : (
        <Box
          mt={0.75}
          sx={{
            maxHeight: 'min(320px, 40vh)',
            overflowY: 'auto',
            pr: 0.5,
          }}
        >
          <Stack spacing={1} divider={<Divider />}>
            {items.map((item) => (
              <Stack
                key={item.id}
                direction="row"
                alignItems="flex-start"
                spacing={1}
                sx={{ py: 0.25 }}
              >
                <Stack sx={{ pt: 0.25 }}>{fileIcon(item)}</Stack>
                <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.25}>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {item.name}
                  </Typography>
                  <Button
                    size="small"
                    sx={{ alignSelf: 'flex-start', px: 0, minWidth: 0 }}
                    onClick={() => onAction(item)}
                  >
                    {actionLabel}
                  </Button>
                  {item.contentFragment && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}
                    >
                      <HighlightText text={item.contentFragment} highlight={activeSearch} />
                    </Typography>
                  )}
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </Paper>
  );
};
